import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import api from '../../services/api';

function formatCurrency(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCurrencyWhole(value) {
  const n = Number(value) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatGallons(value) {
  const n = Number(value) || 0;
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} gal`;
}

function formatCount(value) {
  return String(Number(value) || 0);
}

function TrendChange({ pct }) {
  if (pct == null) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <TrendingFlatIcon sx={{ fontSize: 16 }} /> vs prior month
      </Typography>
    );
  }
  const up = pct > 0;
  const down = pct < 0;
  const color = up ? 'success.main' : down ? 'error.main' : 'text.secondary';
  const Icon = up ? TrendingUpIcon : down ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Typography variant="caption" sx={{ color, display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Icon sx={{ fontSize: 16 }} />
      {pct > 0 ? '+' : ''}
      {pct}% vs prior month
    </Typography>
  );
}

function TrendBarChart({ periods, dataKey, formatValue, color = 'primary.main' }) {
  if (!periods?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data for this period.
      </Typography>
    );
  }
  const max = Math.max(...periods.map((p) => Number(p[dataKey]) || 0), 1);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: { xs: 0.5, sm: 1 },
        height: 200,
        pt: 1,
        overflowX: 'auto',
      }}
    >
      {periods.map((p) => {
        const value = Number(p[dataKey]) || 0;
        const heightPct = Math.max(4, (value / max) * 100);
        return (
          <Box
            key={`${p.year}-${p.month}-${dataKey}`}
            sx={{
              flex: '1 1 0',
              minWidth: 44,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: '0.65rem', lineHeight: 1.2 }}>
              {formatValue(value)}
            </Typography>
            <Box
              sx={{
                mx: 'auto',
                width: '100%',
                maxWidth: 56,
                height: `${heightPct}%`,
                minHeight: value > 0 ? 8 : 4,
                maxHeight: 140,
                bgcolor: color,
                borderRadius: '4px 4px 0 0',
                opacity: value > 0 ? 1 : 0.25,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, display: 'block', fontSize: '0.65rem', lineHeight: 1.1 }}
            >
              {p.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ManagerReportsSection({
  customerId = null,
  customerName = '',
  driverId = null,
  driverName = '',
  vehicleId = null,
  vehicleName = '',
  embedded = false,
  reportsApiPath = '/managers/reports/',
  selfView = false,
}) {
  const [months, setMonths] = useState(6);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const params = { months };
        if (!selfView && customerId) params.customer_id = customerId;
        if (!selfView && driverId) params.driver_id = driverId;
        if (!selfView && vehicleId) params.vehicle_id = vehicleId;
        const res = await api.get(reportsApiPath, { params });
        if (!cancelled) setReport(res.data);
      } catch (err) {
        if (!cancelled) {
          const status = err.response?.status;
          const detail =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            (status === 404
              ? 'Reports API not found. Restart the Django backend to load the latest reports endpoint.'
              : 'Failed to load reports.');
          setError(detail);
          setReport(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [months, customerId, driverId, vehicleId, reportsApiPath, selfView]);

  const summary = report?.summary || {};
  const periods = report?.periods || [];

  const subtitle = selfView
    ? 'Your monthly deliveries, fuel served, and invoiced amounts.'
    : vehicleId
      ? `Monthly revenue, deliveries, and fuel for ${vehicleName || 'this vehicle'}.`
      : driverId
        ? `Monthly revenue, deliveries, and fuel for ${driverName || 'this driver'}.`
        : customerId
          ? `Monthly revenue, deliveries, and fuel for ${customerName || 'this customer'}.`
          : 'Monthly revenue, deliveries completed, and fuel served.';

  return (
    <Paper sx={{ p: embedded ? 2 : 3, mb: embedded ? 0 : 3 }} variant={embedded ? 'outlined' : 'elevation'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant={embedded ? 'h6' : 'h5'}
            component={embedded ? 'h2' : 'h1'}
            gutterBottom
            sx={{ mb: 0.5, fontWeight: 700 }}
          >
            Reports &amp; Trends
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="report-months-label">Period</InputLabel>
          <Select
            labelId="report-months-label"
            label="Period"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            <MenuItem value={3}>Last 3 months</MenuItem>
            <MenuItem value={6}>Last 6 months</MenuItem>
            <MenuItem value={12}>Last 12 months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Revenue collected
                  </Typography>
                  <Typography variant="h5">{formatCurrencyWhole(summary.revenue_total)}</Typography>
                  <TrendChange pct={summary.revenue_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Invoiced
                  </Typography>
                  <Typography variant="h5">{formatCurrencyWhole(summary.invoiced_total)}</Typography>
                  <TrendChange pct={summary.invoiced_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Jobs completed
                  </Typography>
                  <Typography variant="h5">{formatCount(summary.jobs_completed_total)}</Typography>
                  <TrendChange pct={summary.jobs_completed_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    Diesel delivered
                  </Typography>
                  <Typography variant="h5">{formatGallons(summary.diesel_gallons_total)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fuel value: {formatCurrency(summary.diesel_fuel_value_total)}
                  </Typography>
                  <TrendChange pct={summary.diesel_gallons_change_pct} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="body2" color="text.secondary">
                    DEF delivered
                  </Typography>
                  <Typography variant="h5">{formatGallons(summary.def_gallons_total)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Fuel value: {formatCurrency(summary.def_fuel_value_total)}
                  </Typography>
                  <TrendChange pct={summary.def_fuel_value_change_pct} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Revenue collected (paid invoices)
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="revenue"
                formatValue={formatCurrencyWhole}
                color="#2E7D32"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Invoiced amount
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="invoiced"
                formatValue={formatCurrencyWhole}
                color="#1565C0"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Diesel delivered
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="diesel_gallons"
                formatValue={formatGallons}
                color="#F4511E"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                DEF fuel value
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="def_fuel_value"
                formatValue={formatCurrency}
                color="#7B1FA2"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Diesel fuel value
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="diesel_fuel_value"
                formatValue={formatCurrency}
                color="#E64A19"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Jobs completed
              </Typography>
              <TrendBarChart
                periods={periods}
                dataKey="jobs_completed"
                formatValue={formatCount}
                color="#455A64"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
}
